/**
 * Subscriptions API Service (TASK-061)
 *
 * API client functions for subscription management.
 */
import axios from 'axios';
import apiClient from './client';
import type {
  SubscriptionPlan,
  OrganizationSubscription,
  SubscriptionUsage,
  CreatePaymentPayload,
  CreatePaymentResponse,
  CancelSubscriptionResponse,
} from '../../types/subscription';

export const subscriptionApi = {
  /**
   * Get all available subscription plans
   */
  getPlans: async (): Promise<SubscriptionPlan[]> => {
    const response = await apiClient.get<SubscriptionPlan[]>(
      '/api/v1/subscription-plans/'
    );
    return response.data;
  },

  /**
   * Get current organization subscription
   */
  getCurrent: async (): Promise<OrganizationSubscription | null> => {
    try {
      const response = await apiClient.get<OrganizationSubscription>(
        '/api/v1/subscription-plans/current/'
      );
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error) && error.response?.status === 404) {
        return null;
      }
      throw error;
    }
  },

  /**
   * Get current usage/quota information
   */
  getUsage: async (): Promise<SubscriptionUsage> => {
    const response = await apiClient.get<SubscriptionUsage>(
      '/api/v1/quotas/current/'
    );
    return response.data;
  },

  /**
   * Create a payment for plan change
   */
  createPayment: async (
    data: CreatePaymentPayload
  ): Promise<CreatePaymentResponse> => {
    const response = await apiClient.post<CreatePaymentResponse>(
      '/api/v1/payments/create/',
      data
    );
    return response.data;
  },

  /**
   * Cancel a subscription
   */
  cancel: async (subscriptionId: string): Promise<CancelSubscriptionResponse> => {
    const response = await apiClient.post<CancelSubscriptionResponse>(
      `/api/v1/subscription-plans/${subscriptionId}/cancel/`
    );
    return response.data;
  },
};

export default subscriptionApi;
